import React, { useState, useEffect, useMemo } from 'react';
import styled, { css } from 'styled-components';
import tw from 'twin.macro';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useMutation } from '@apollo/client';
import {
  GetMyCarsV2LeanItem,
  useGetMyCarsV2LeanQuery,
} from '../../services/carService/getMyCarsV2';
import {
  REQUEST_CAR_SWAP,
  GET_BOOKING_DETAILS,
  GET_BOOKING_ACTIVITIES,
} from '../../services/bookingService/queries';
import { Button } from '../button';
import { DateTimeRangePicker } from '../DateTimeRangePicker';
import { DateTime } from 'luxon';
import { toast } from 'react-toastify';
import { URLS } from '../../../constants/urls';
import { ScaleLoader } from 'react-spinners';
import { Colors } from '../../styles/colors';
import { IGetBookingQuery } from '../../services/bookingService/__generated__/queries.generated';
import {
  getNowInTimezone,
  getNextHourInTimezone,
  getFilteredTimeOptions,
  convert24To12Hour,
} from '../../helpers/timezoneHelpers';
import { mergePagedItems } from '../../utils/hostListingsPagination';
import {
  filterSwapCandidateCars,
  formatLeanCarDisplayLabel,
} from '../../utils/leanCarDisplayLabel';

const ModalOverlay = styled.div`
  ${tw`fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-40`}
`;

const ModalContent = styled.div`
  ${tw`bg-white rounded-lg p-6 w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto`}
`;

const ModalHeader = styled.div`
  ${tw`flex justify-between items-center mb-4`}
`;

const Title = styled.h2`
  ${tw`text-xl font-bold`}
`;

const CloseButton = styled.button`
  ${tw`text-text-secondary`}
  &:hover {
    ${tw`text-text-secondary`}
  }
`;

const Subtitle = styled.p`
  ${tw`text-sm text-text-secondary mb-4`}
`;

const SectionTitle = styled.h3`
  ${tw`text-lg font-semibold mb-3 mt-4`}
`;

const CarList = styled.div`
  ${tw`grid grid-cols-1 md:grid-cols-2 gap-4 mb-4`}
`;

const CarCard = styled.div<{ isSelected: boolean }>`
  ${tw`border-2 rounded-lg p-4 cursor-pointer transition-all`}
  ${({ isSelected }) => isSelected && tw`border-gray-900 bg-bg-secondary`}
  ${({ isSelected }) => !isSelected && tw`border-border-default`}
  ${({ isSelected }) =>
    !isSelected &&
    css`
      &:hover {
        ${tw`border-border-default`}
      }
    `}
`;

const CarImage = styled.img`
  ${tw`w-full h-32 object-cover rounded-md mb-2`}
`;

const CarName = styled.div`
  ${tw`font-semibold text-lg mb-1`}
`;

const CarInfo = styled.div`
  ${tw`text-sm text-text-secondary`}
`;

const NoteTextarea = styled.textarea`
  ${tw`w-full border border-border-default rounded-lg p-3 mb-4`}
  min-height: 100px;
`;

const ButtonGroup = styled.div`
  ${tw`flex justify-end gap-4 mt-6`}
`;

const LoadingContainer = styled.div`
  ${tw`flex justify-center items-center py-8`}
`;

interface CarSwapModalProps {
  isOpen: boolean;
  onClose: () => void;
  booking: IGetBookingQuery['booking'];
  onSwapRequested: () => void;
}

export const CarSwapModal: React.FC<CarSwapModalProps> = ({
  isOpen,
  onClose,
  booking,
  onSwapRequested,
}) => {
  const [selectedCarId, setSelectedCarId] = useState<string | null>(null);
  const [note, setNote] = useState('');
  const [swapStartTime, setSwapStartTime] = useState<number | undefined>(undefined);
  const [timeValidationError, setTimeValidationError] = useState<string | null>(null);
  const [selectedSwapDate, setSelectedSwapDate] = useState<Date | null>(null);

  // Get car's timezone (use car's location, not user's browser timezone)
  const carTimezone = booking.car?.location?.timezone || 'America/Los_Angeles';

  // Check if trip has started
  // Normalize timestamps to milliseconds for robust comparison during transition
  const normalizeTimestamp = (ts: number | string | undefined | null) => {
    if (!ts) return 0;
    const numValue = Number(ts);
    // If it looks like seconds (10 digits), convert to milliseconds
    if (numValue > 0 && numValue < 10000000000) {
      return numValue * 1000;
    }
    return numValue;
  };

  const bookingStartAt = normalizeTimestamp(booking.startAt);
  const bookingEndAt = normalizeTimestamp(booking.endAt);
  const tripStarted = bookingStartAt < Date.now();

  const [carsPage, setCarsPage] = useState(1);
  const [loadedCars, setLoadedCars] = useState<GetMyCarsV2LeanItem[]>([]);

  useEffect(() => {
    if (!isOpen) {
      setCarsPage(1);
      setLoadedCars([]);
    }
  }, [isOpen]);

  const {
    data: carsData,
    loading: carsLoading,
    error: carsError,
    refetch: refetchCars,
  } = useGetMyCarsV2LeanQuery({
    skip: !isOpen,
    variables: { input: { page: carsPage, limit: 20 } },
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (!isOpen) return;
    const paged = carsData?.getMyCarsV2;
    if (!paged || paged.page !== carsPage) return;
    setLoadedCars(prev => mergePagedItems(prev, paged.page, paged.items));
  }, [carsData, carsPage, isOpen]);

  const currentCarId = booking.car?.id;
  const cars = filterSwapCandidateCars(loadedCars, currentCarId);
  const carsHasMore =
    carsData?.getMyCarsV2?.page === carsPage && carsData.getMyCarsV2.hasMore === true;

  // Initialize selected swap date when modal opens (using car's timezone)
  useEffect(() => {
    if (isOpen && !selectedSwapDate) {
      if (tripStarted) {
        // Use current time in car's timezone
        const nowInCarTimezone = getNowInTimezone(carTimezone);
        setSelectedSwapDate(nowInCarTimezone.toJSDate());
      } else {
        setSelectedSwapDate(new Date(booking.startAt));
      }
    }
  }, [isOpen, tripStarted, booking.startAt, selectedSwapDate, carTimezone]);

  const [requestCarSwap, { loading: requestLoading }] = useMutation(REQUEST_CAR_SWAP, {
    refetchQueries: [
      { query: GET_BOOKING_DETAILS, variables: { id: booking.id } },
      { query: GET_BOOKING_ACTIVITIES, variables: { bookingId: booking.id } },
    ],
    onCompleted: () => {
      toast.success('Car swap request sent successfully');
      onSwapRequested();
      onClose();
    },
    onError: error => {
      toast.error(error.message || 'Failed to request car swap');
    },
  });

  // Note: Availability check is handled by the backend when submitting the swap request
  // We let the backend validate availability to avoid unnecessary API calls
  // The backend will return an error if the car is not available

  // Handle swap start time change for mid-trip swaps
  const handleSwapStartTimeChange = (dates: {
    startDate: Date;
    endDate: Date;
    startTime: string;
    endTime: string;
    startTimestamp: number;
    endTimestamp: number;
  }) => {
    setSwapStartTime(dates.startTimestamp);
    setSelectedSwapDate(dates.startDate);

    // Validate the time
    const now = Date.now();
    if (dates.startTimestamp < now) {
      setTimeValidationError('Swap start time must be in the future');
    } else if (dates.startTimestamp < bookingStartAt) {
      setTimeValidationError('Swap start time cannot be before the booking start time');
    } else if (dates.startTimestamp >= bookingEndAt) {
      setTimeValidationError('Swap start time must be before the booking end time');
    } else {
      setTimeValidationError(null);
    }
  };

  // Get initial swap start time (current time for mid-trip swaps)
  // Memoize to prevent creating new Date objects on every render
  const initialSwapStartDate = useMemo(() => {
    return tripStarted ? new Date() : new Date(booking.startAt);
  }, [tripStarted, booking.startAt]);

  const initialSwapStartTime = useMemo(() => {
    if (tripStarted) {
      // For mid-trip swaps, set to next hour in car's timezone (already in 12-hour format)
      return getNextHourInTimezone(carTimezone);
    }
    const time24 = DateTime.fromMillis(booking.startAt).setZone(carTimezone).toFormat('HH:mm');
    return convert24To12Hour(time24);
  }, [tripStarted, booking.startAt, carTimezone]);

  const initialSwapEndDate = useMemo(() => {
    return new Date(booking.endAt);
  }, [booking.endAt]);

  const initialSwapEndTime = useMemo(() => {
    const time24 = DateTime.fromMillis(booking.endAt).setZone(carTimezone).toFormat('HH:mm');
    return convert24To12Hour(time24);
  }, [booking.endAt, carTimezone]);

  // Generate time options based on selected date
  // If today is selected, only show future times (next hour onwards)
  // IMPORTANT: Use car's timezone, not user's browser timezone
  const startAtOptions = useMemo(() => {
    const dateToCheck = selectedSwapDate || initialSwapStartDate;
    return getFilteredTimeOptions(dateToCheck, carTimezone);
  }, [selectedSwapDate, initialSwapStartDate, carTimezone]);

  const handleSubmit = async () => {
    if (!selectedCarId) {
      toast.error('Please select one of your cars to continue');
      return;
    }

    // Validate swap start time is in the future (for mid-trip swaps)
    if (tripStarted && swapStartTime) {
      const now = Date.now(); // Current time in milliseconds
      if (swapStartTime < now) {
        toast.error('Swap start time must be in the future');
        return;
      }
      if (swapStartTime < bookingStartAt) {
        toast.error('Swap start time cannot be before the booking start time');
        return;
      }
      if (swapStartTime >= bookingEndAt) {
        toast.error('Swap start time must be before the booking end time');
        return;
      }
    }

    try {
      await requestCarSwap({
        variables: {
          input: {
            bookingId: booking.id,
            newCarId: selectedCarId,
            note: note || undefined,
            swapStartTime: swapStartTime ? swapStartTime : undefined,
          },
        },
      });
    } catch (error) {
      // Error is handled by onError callback
    }
  };

  if (!isOpen) return null;

  return (
    <ModalOverlay onClick={onClose}>
      <ModalContent onClick={e => e.stopPropagation()}>
        <ModalHeader>
          <Title>Swap Car</Title>
          <CloseButton onClick={onClose}>
            <FontAwesomeIcon icon={faTimes} />
          </CloseButton>
        </ModalHeader>

        <Subtitle>
          Select a replacement car for this booking. The guest will need to approve the swap.
        </Subtitle>

        {/* Mid-trip swap: Show date/time picker */}
        {tripStarted && (
          <>
            <SectionTitle>Swap Start Time</SectionTitle>
            <Subtitle>
              Select when the new car will take over. The swap will last until the original trip end
              time.
            </Subtitle>
            <DateTimeRangePicker
              initialStartDate={initialSwapStartDate}
              initialEndDate={initialSwapEndDate}
              initialStartTime={initialSwapStartTime}
              initialEndTime={initialSwapEndTime}
              startAtOptions={startAtOptions}
              endAtOptions={startAtOptions}
              onDateTimeChange={handleSwapStartTimeChange}
              carId={selectedCarId || undefined}
              excludeBookingId={booking.id}
              timezone={carTimezone}
              startDisabled={false}
              endDisabled={true}
            />
            {timeValidationError && (
              <div
                style={{
                  color: Colors.Error,
                  fontSize: '14px',
                  marginTop: '8px',
                  padding: '8px',
                  backgroundColor: Colors.LightRed,
                  borderRadius: '6px',
                  border: `1px solid ${Colors.Error}`,
                }}
              >
                {timeValidationError}
              </div>
            )}
          </>
        )}

        {/* Car selection */}
        <SectionTitle>Select Replacement Car</SectionTitle>
        {carsLoading && loadedCars.length === 0 ? (
          <LoadingContainer>
            <ScaleLoader color="var(--color-black)" />
          </LoadingContainer>
        ) : carsError && loadedCars.length === 0 ? (
          <Subtitle role="alert">
            Unable to load your cars.{' '}
            <button type="button" className="underline" onClick={() => void refetchCars()}>
              Retry
            </button>
          </Subtitle>
        ) : cars.length === 0 && !carsHasMore ? (
          <Subtitle>You don't have any other cars available for swapping.</Subtitle>
        ) : (
          <CarList>
            {cars.map(car => (
              <CarCard
                key={car.id}
                isSelected={selectedCarId === car.id}
                onClick={() => setSelectedCarId(car.id)}
              >
                <CarImage
                  src={
                    car.thumbnailUrl
                      ? `${URLS.S3_BUCKET.CAR_THUMBNAILS}/${car.thumbnailUrl}`
                      : `${URLS.S3_BUCKET.CAR_THUMBNAILS}/car-images/default-car.jpg`
                  }
                  alt={formatLeanCarDisplayLabel(car)}
                />
                <CarName>{formatLeanCarDisplayLabel(car)}</CarName>
                <CarInfo>
                  {car.year} {car.make} {car.model}
                </CarInfo>
              </CarCard>
            ))}
            {cars.length === 0 && carsHasMore && (
              <Subtitle>No other cars on this page. Load more to see additional listings.</Subtitle>
            )}
            {carsError && (
              <Subtitle role="alert">
                Unable to load more cars.{' '}
                <button type="button" className="underline" onClick={() => void refetchCars()}>
                  Retry
                </button>
              </Subtitle>
            )}
            {carsHasMore && (
              <Button
                text={carsLoading ? 'Loading…' : 'Load more cars'}
                theme="outlined"
                isDisabled={carsLoading}
                onClick={() => setCarsPage(p => p + 1)}
                dataTestId="car-swap-load-more"
              />
            )}
          </CarList>
        )}

        {/* Note */}
        <SectionTitle>Note (Optional)</SectionTitle>
        <NoteTextarea
          value={note}
          onChange={e => setNote(e.target.value)}
          placeholder="Add a note for the guest about this swap..."
          maxLength={500}
        />

        <ButtonGroup>
          <Button text="Cancel" theme="outlined" onClick={onClose} />
          <Button
            text="Request Swap"
            onClick={handleSubmit}
            isLoading={requestLoading}
            isDisabled={requestLoading}
          />
        </ButtonGroup>
      </ModalContent>
    </ModalOverlay>
  );
};
